import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/status/status-badge";
import { api } from "@/lib/api";
import { destinationLifecycleTone } from "@/components/status/status-badge";
import {
  FilterField,
  FilterToolbar,
  OverriddenBadge,
  PaginationBar,
  SearchInput,
  billingStatusTone,
  formatUnix,
  formatUnixDate,
} from "./shared";

type AccountRow = {
  id: string;
  name: string;
  accountType?: string;
  conferences: number;
  streams: number;
  activeStreams?: number;
  members?: number;
  cancelledAt?: number | null;
  billingStatus?: string | null;
  adminAccess?: string | null;
  attendeesThisPeriod?: number;
  trialEndsAt?: number | null;
  planCode?: string | null;
};

type AccountDetail = {
  account: {
    id: string;
    name: string;
    accountType: string;
    trialEndsAt: number | null;
    createdAt: number;
    cancelledAt: number | null;
  };
  members: Array<{
    userId: string;
    email: string;
    displayName: string;
    role: string;
    createdAt: number;
  }>;
  streams: Array<{
    id: string;
    name: string;
    lifecycle: string;
    conferenceId: string;
    conferenceName?: string | null;
    sourceProvider: string;
    destinationProvider: string;
  }>;
  quotas: {
    planDefaults: Record<string, number>;
    overrides: Record<string, unknown> | null;
    effective: {
      maxConferences: number;
      maxStreams: number;
      maxRunsPerDay: number;
      attendeesPerStream: number | null;
    };
    planCode: string;
    isOverridden: boolean;
  };
  subscription: {
    status: string;
    trialEndsAt: number | null;
    stripeSubscriptionId: string | null;
    currentPeriodStart: number;
    currentPeriodEnd: number;
    adminAccess: string;
    adminAccessUntil: number | null;
    adminAccessNote: string | null;
  };
  usage: {
    conferences: number;
    streams: number;
    activeStreams: number;
    runsToday: number;
    attendeesThisPeriod: number;
  };
};

export function SuperAdminAccountsPage() {
  const [qDraft, setQDraft] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (q) params.set("q", q);
    void api<{ accounts: AccountRow[]; pagination: { total: number } }>(
      `/api/platform/accounts?${params}`,
    )
      .then((r) => {
        setAccounts(r.accounts ?? []);
        setTotal(r.pagination?.total ?? 0);
      })
      .catch(() => {
        setAccounts([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [q, offset]);

  const page = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-rs-ink">Accounts</h1>
        <p className="text-sm text-rs-ink-muted">
          Private workspaces with billing and usage summary.
        </p>
      </div>
      <FilterToolbar>
        <FilterField
          id="sa-orgs-q"
          label="Search"
          className="min-w-[14rem] flex-1 space-y-1"
        >
          <SearchInput
            id="sa-orgs-q"
            value={qDraft}
            onChange={setQDraft}
            placeholder="Account name or id"
          />
        </FilterField>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setQ(qDraft.trim());
            setOffset(0);
          }}
        >
          Search
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setQDraft("");
            setQ("");
            setOffset(0);
          }}
        >
          Clear
        </Button>
      </FilterToolbar>
      {loading ? (
        <PageSkeleton />
      ) : (
        <>
          <Panel>
            <ul className="divide-y divide-rs-border">
              {accounts.map((a) => (
                <li key={a.id}>
                  <Link
                    to={`/superadmin/accounts/${a.id}`}
                    className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 hover:bg-rs-muted"
                  >
                    <div>
                      <p className="font-medium text-rs-ink">{a.name}</p>
                      <p className="text-sm text-rs-ink-muted">
                        {a.id} · {a.members ?? 0} members ·{" "}
                        {a.activeStreams ?? 0} active streams
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {a.cancelledAt ? (
                        <StatusBadge tone="failed">Cancelled</StatusBadge>
                      ) : (
                        <StatusBadge tone={billingStatusTone(a.billingStatus)}>
                          {a.billingStatus ?? "none"}
                        </StatusBadge>
                      )}
                      {a.adminAccess && a.adminAccess !== "none" ? (
                        <StatusBadge tone="warning">Admin Access</StatusBadge>
                      ) : null}
                    </div>
                  </Link>
                </li>
              ))}
              {accounts.length === 0 ? (
                <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
                  No accounts found.
                </li>
              ) : null}
            </ul>
          </Panel>
          <PaginationBar
            page={page}
            limit={limit}
            total={total}
            onPageChange={(p) => setOffset((p - 1) * limit)}
          />
        </>
      )}
    </div>
  );
}

export function SuperAdminAccountDetailPage() {
  const { accountId = "" } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<AccountDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void api<AccountDetail>(
      `/api/platform/accounts/${encodeURIComponent(accountId)}`,
    )
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [accountId]);

  if (loading) return <PageSkeleton />;
  if (!data) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold text-rs-ink">Account Not Found</h1>
        <Button
          variant="secondary"
          onClick={() => navigate("/superadmin/accounts")}
        >
          Back to Accounts
        </Button>
      </div>
    );
  }

  const { account, members, streams, quotas, subscription, usage } = data;

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm text-rs-ink-muted">
          <Link
            className="text-rs-accent hover:underline"
            to="/superadmin/accounts"
          >
            Accounts
          </Link>
          {" / "}
          {account.name}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-semibold text-rs-ink">{account.name}</h1>
          <StatusBadge tone={billingStatusTone(subscription.status)}>
            {subscription.status}
          </StatusBadge>
          {account.cancelledAt ? (
            <StatusBadge tone="failed">Cancelled</StatusBadge>
          ) : null}
          <OverriddenBadge show={quotas.isOverridden} />
        </div>
        <p className="text-sm text-rs-ink-muted">
          Created {formatUnix(account.createdAt)}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Members", value: members.length },
          { label: "Conferences", value: usage.conferences },
          { label: "Active Streams", value: usage.activeStreams },
          { label: "Attendees This Period", value: usage.attendeesThisPeriod },
        ].map((m) => (
          <Panel key={m.label} className="p-4">
            <p className="text-sm text-rs-ink-muted">{m.label}</p>
            <p className="mt-1 text-xl font-semibold text-rs-ink">
              {m.value.toLocaleString()}
            </p>
          </Panel>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel>
          <PanelHeader title="Subscription" />
          <dl className="space-y-2 px-5 py-4 text-sm">
            <div className="flex justify-between gap-2">
              <dt className="text-rs-ink-muted">Status</dt>
              <dd>{subscription.status}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-rs-ink-muted">Trial Ends</dt>
              <dd>{formatUnixDate(subscription.trialEndsAt)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-rs-ink-muted">Period</dt>
              <dd>
                {formatUnixDate(subscription.currentPeriodStart)} –{" "}
                {formatUnixDate(subscription.currentPeriodEnd)}
              </dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-rs-ink-muted">Admin Access</dt>
              <dd>
                {subscription.adminAccess}
                {subscription.adminAccessUntil
                  ? ` until ${formatUnixDate(subscription.adminAccessUntil)}`
                  : ""}
              </dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-rs-ink-muted">Stripe</dt>
              <dd className="font-mono text-xs">
                {subscription.stripeSubscriptionId ?? "—"}
              </dd>
            </div>
          </dl>
          <div className="border-t border-rs-border px-5 py-3">
            <Link
              className="text-sm text-rs-accent hover:underline"
              to={`/superadmin/subscriptions/${account.id}`}
            >
              Manage Subscription
            </Link>
          </div>
        </Panel>

        <Panel>
          <PanelHeader
            title="Quotas"
            description={`Plan ${quotas.planCode}`}
            titleAside={<OverriddenBadge show={quotas.isOverridden} />}
          />
          <dl className="space-y-2 px-5 py-4 text-sm">
            {(
              [
                [
                  "Max Conferences",
                  quotas.effective.maxConferences,
                  quotas.planDefaults.maxConferences,
                ],
                [
                  "Max Streams",
                  quotas.effective.maxStreams,
                  quotas.planDefaults.maxStreams,
                ],
                [
                  "Max Runs / Day",
                  quotas.effective.maxRunsPerDay,
                  quotas.planDefaults.maxRunsPerDay,
                ],
                [
                  "Attendees / Stream",
                  quotas.effective.attendeesPerStream ?? "plan default",
                  "—",
                ],
              ] as const
            ).map(([label, effective, plan]) => (
              <div key={label} className="flex justify-between gap-2">
                <dt className="text-rs-ink-muted">{label}</dt>
                <dd>
                  {String(effective)}
                  {effective !== plan ? (
                    <span className="ml-1 text-xs text-rs-ink-subtle">
                      (plan {String(plan)})
                    </span>
                  ) : null}
                </dd>
              </div>
            ))}
          </dl>
          <div className="border-t border-rs-border px-5 py-3">
            <Link
              className="text-sm text-rs-accent hover:underline"
              to={`/superadmin/usage?accountId=${encodeURIComponent(account.id)}`}
            >
              Edit Quotas
            </Link>
          </div>
        </Panel>
      </div>

      <Panel>
        <PanelHeader title="Members" />
        <ul className="divide-y divide-rs-border">
          {members.map((m) => (
            <li
              key={m.userId}
              className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm"
            >
              <div>
                <Link
                  className="font-medium text-rs-accent hover:underline"
                  to={`/superadmin/users/${m.userId}`}
                >
                  {m.displayName}
                </Link>
                <p className="text-rs-ink-muted">{m.email}</p>
              </div>
              <StatusBadge tone="neutral">{m.role}</StatusBadge>
            </li>
          ))}
          {members.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
              No members.
            </li>
          ) : null}
        </ul>
      </Panel>

      <Panel>
        <PanelHeader title="Streams" />
        <ul className="divide-y divide-rs-border">
          {streams.map((s) => (
            <li
              key={s.id}
              className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm"
            >
              <div>
                <Link
                  className="font-medium text-rs-accent hover:underline"
                  to={`/superadmin/streams/${s.id}`}
                >
                  {s.name}
                </Link>
                <p className="text-rs-ink-muted">
                  {s.conferenceName ?? s.conferenceId} · {s.sourceProvider} →{" "}
                  {s.destinationProvider}
                </p>
              </div>
              <StatusBadge tone={destinationLifecycleTone(s.lifecycle)}>
                {s.lifecycle}
              </StatusBadge>
            </li>
          ))}
          {streams.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
              No streams.
            </li>
          ) : null}
        </ul>
      </Panel>
    </div>
  );
}
